<template>
  <transition name="fade" mode="out-in">
    <div class="col-span-full sm:col-span-full md:col-span-7 lg:col-span-7">
      <h1 class="text-t1 mb-4">Profil</h1>

      <transition v-if="!!lockedToEdit || this.claims.indexOf('company') > -1" name="fade" mode="out-in">
        <div class="mb-4 rounded-[15px] bg-primary-10 p-4 border-[1px] border-solid border-gray-light">
          <h2 class="font-neue-kabel font-thick mb-2 leading-[120%]">Behöver du ändra någon uppgift?</h2>
          <p class="leading-[154%]">Om du behöver ändra informationen kopplat till din profl så är du välkommen att kontakta oss på kundcentrum tel: <br />026-17 27 00.</p>
        </div>
      </transition>

      <div class="col-span-full flex flex-col gap-8 pb-8 md:col-span-7">
        <ContactSection
          :profile="editableProfile"
          :canEdit="canEditContact"
          :editable="canEditContact && editingSection === 'contact'"
          :editDisabled="isEditingOther('contact')"
          @edit="editingSection = 'contact'"
          @cancel="editingSection = null"
          @save="handleSaveContactSection"
          :isSaving="isSaving"
          :saveError="saveError"
          :hasContract="hasActiveOrComingContract"
        />

        <DoorNameSection
          v-if="contractNumber && contractNumber.length >= 1 && hasApartment && hasActiveOrTerminatedResidentalContract"
          :initialValue="editableProfile.nameOnDoor"
          :canEdit="!lockedToEdit"
          :editable="!lockedToEdit && editingSection === 'doorName'"
          :editDisabled="isEditingOther('doorName')"
          :contractNumber="contractNumber"
          @edit="editingSection = 'doorName'"
          @cancel="editingSection = null"
          @save="handleSaveDoorName"
        />

        <IntercomSection
          v-if="contractNumber && contractNumber.length >= 1 && hasApartment && hasActiveOrTerminatedResidentalContract"
          :initialEntries="editableProfile.intercomEntries || []"
          :canEdit="!lockedToEdit"
          :editable="!lockedToEdit && editingSection === 'intercom'"
          :editDisabled="isEditingOther('intercom')"
          :contractNumber="contractNumber"
          @edit="editingSection = 'intercom'"
          @cancel="editingSection = null"
          @save="handleSaveIntercom"
        />

        <LoginMethodsSection @change-password="isShowPassword = true" />
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from 'vuex';
import ContactSection from '@/pages/profile/contact-section';
import DoorNameSection from '@/pages/profile/door-name-section.vue';
import IntercomSection from '@/pages/profile/intercom-section.vue';
import LoginMethodsSection from '@/pages/profile/login-methods-section';
import { CONTRACT_STATUSES } from '@/config/constants'
import Loader from '@/components/ui/loader';
import { filter } from 'lodash';
import { RENTALOBJECT_TYPES } from '@/config/constants';
import { dataLayerPush } from '@/helpers/datalayer';

export default {
  components: {
    Loader,
    ContactSection,
    DoorNameSection,
    IntercomSection,
    LoginMethodsSection,
  },

  data() {
    return {
      showLoader: true,
      isShow: false,
      isShowPassword: false,
      editableProfile: {},
      contractNumber: null,
      saveError: null,
      isSaving: false,
      // Vilken sektion som redigeras just nu (contact, doorName, intercom). Bara en åt gången.
      editingSection: null,
    };
  },

  created() {
    window.scroll(0, 0);

    return Promise.all([
      this.$store.cache.dispatch('fetchProfile'),
      this.$store.cache.dispatch('getDashboard'),
      this.$store.cache.dispatch('fetchContracts'),
      this.$store.cache.dispatch('fetchPasswordRules'),
    ])
      .then(() => {
        const rawProfile = this.$store.getters.profile;
        const contracts = Object.values(JSON.parse(JSON.stringify(this.$store.getters.contracts)));
        const apartmentContract = filter(
          contracts,
          (c) => c.rentalObjectType === RENTALOBJECT_TYPES.APARTMENT && c.status != CONTRACT_STATUSES.COMING && c.status != 'ceased' && c.status != CONTRACT_STATUSES.AWAITING_SIGNATURE,
        );
        const contractNumber = apartmentContract;

        this.editableProfile = { ...rawProfile };
        this.contractNumber = contractNumber;

        const storeData = this.$store.getters.getNamesOnDoorByContractNumber(this.contractNumber);

        this.editableProfile.intercomEntries = storeData?.intercomes || [];
        this.editableProfile.nameOnDoor = storeData?.NameOnDoor || '';
      })
      .finally(() => {
        this.showLoader = false;
      });
  },

  computed: {
    ...mapGetters({
      profile: 'profile',
      hasApartment: 'hasApartment',
      claims: 'claims',
      hasActiveOrComingContract: 'hasActiveOrComingContract',
      hasActiveOrTerminatedResidentalContract: 'hasActiveOrTerminatedResidentalContract'
    }),

    canEditContact() {
      return !this.lockedToEdit && this.claims.indexOf('company') == -1;
    },

    lockedToEdit() {
      if (this.claims.indexOf('preSystemLocked') !== -1 || (this.claims.indexOf('company') !== -1 && !this.hasActiveOrTerminatedResidentalContract)) return true;
      return false;
    },
  },

  methods: {
    isEditingOther(section) {
      return this.editingSection !== null && this.editingSection !== section;
    },

    async handleSaveContactSection(updatedContactData) {
      const emailChanged =
        (updatedContactData.email || '').trim() !== (this.$store.getters.profile.email || '').trim();

      try {
        this.saveError = null;
        this.isSaving = true;

        await this.$store.dispatch('saveProfile', { ...this.editableProfile, ...updatedContactData });
        dataLayerPush({ event: 'editProfile' });

        // Byte av e-postadress kräver ny inloggning. Därefter skickas ett verifieringsmejl till den nya adressen.
        if (emailChanged) {
          this.$store.dispatch('logout');
          return;
        }

        await this.$store.dispatch('fetchProfile');
        await this.$store.dispatch('confirmDetails');
        this.editableProfile = { ...this.editableProfile, ...this.$store.getters.profile };
        this.editingSection = null;
      } catch (error) {
        const description = error?.response?.data?.description;

        if (description === "Email already in use") this.saveError = "E-postadressen används redan på ett annat konto. Vänligen ange en unik e-postadress."
        else this.saveError = "Något gick fel, försök igen";
        console.error('Fel vid sparande av kontaktuppgifter:', error);
      } finally {
        this.isSaving = false;
      }
    },

    async handleSaveDoorName(newValue) {
      try {
        const payload = {
          ContractNumber: newValue[1],
          NameOnDoor: newValue[0],
          intercomes: newValue[2] || [],
        };


        this.showLoader = true;
        await this.$store.dispatch('saveNamesOnDoor', payload);
        await this.$store.dispatch('fetchProfile');

        const updated = this.$store.getters.profile;
        this.editableProfile = { ...updated };
        this.editingSection = null;
      } catch (err) {
        console.error('Fel vid sparande av namn på dörr:', err);
      } finally {
        this.showLoader = false;
      }
    },

    async handleSaveIntercom(newEntries) {
      try {
        const payload = {
          ContractNumber: newEntries[1],
          NameOnDoor: newEntries[2] || '',
          intercomes: newEntries[0],
        };

        this.showLoader = true;
        await this.$store.dispatch('saveNamesOnDoor', JSON.parse(JSON.stringify(payload)));
        await this.$store.dispatch('fetchProfile');

        const updated = this.$store.getters.profile;

        this.editableProfile = { ...updated };
        this.editingSection = null;
      } catch (err) {
        console.error('Fel vid sparande av porttelefonuppgifter:', err);
      } finally {
        this.showLoader = false;
      }
    },
  },
};
</script>
